import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import { Box } from '@mui/system';

import { SelectDropdown } from '@zendesk/forethought-elements';
import isInternalUi from 'src/components/app/isInternalUi';
import Spinner from 'src/components/spinner/Spinner';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { OrgsDict } from 'src/services/apiInterfaces';
import { getSearchOrganizationsAPI } from 'src/services/apiV1';
import {
  getHelpCenterRedirectUrl,
  shouldRedirectToHelpCenter,
} from 'src/utils/helpCenterAuthUtils';

const ORG_ITEM_KEY = 'org';

/**
 * for SUPER_ADMIN users, ensure that an org is selected before rendering views
 */
export default function WithOrgSelected({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedOrg, setSelectedOrg] = useLocalStorage<OrgsDict | null>(
    ORG_ITEM_KEY,
    null,
  );

  if (!selectedOrg) {
    return <SuperAdminOrgSelectPage setSelectedOrg={setSelectedOrg} />;
  }

  return <>{children}</>;
}
interface SuperAdminOrgSelectPageProps {
  setSelectedOrg: (org: OrgsDict) => void;
}

function SuperAdminOrgSelectPage({
  setSelectedOrg,
}: SuperAdminOrgSelectPageProps) {
  const { t } = useTranslation('authentication-wall');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [orgs, setOrgs] = useState<OrgsDict[] | null>(null);
  const isInternalUI = useMemo(isInternalUi, []);

  useEffect(() => {
    setLoading(true);
    setError(false);

    getSearchOrganizationsAPI()
      .then(({ organizations }) => setOrgs(organizations))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const orgOptions = useMemo(() => {
    return orgs?.map(org => ({
      label: org.org_name,
      value: org.org_id.toString(),
    }));
  }, [orgs]);

  const onOrgSelect = useCallback(
    async (e: SelectChangeEvent) => {
      const selectedOrg = orgs?.find(
        org => org.org_id.toString() === e.target.value,
      );

      if (!selectedOrg) {
        console.error('Selected org not found in list of available orgs');
        return;
      }

      if (shouldRedirectToHelpCenter()) {
        localStorage.setItem(ORG_ITEM_KEY, JSON.stringify(selectedOrg));
        try {
          const redirectUrl = await getHelpCenterRedirectUrl();
          window.location.href = redirectUrl;
          return;
        } catch {}
      }

      setSelectedOrg(selectedOrg);
    },
    [orgs, setSelectedOrg],
  );

  return (
    <Box
      sx={theme => ({
        alignItems: 'center',
        backgroundColor: theme.palette.colors.white,
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        width: '100%',
      })}
    >
      <Box sx={{ maxWidth: 600 }}>
        {loading && <Spinner />}
        {error && isInternalUI && <h2>{t('withOrgSelector.loadError')}</h2>}
        {error && !isInternalUI && <h2>{t('withOrgSelector.retoolReady')}</h2>}
        {!(loading || error) && orgOptions && (
          <>
            <h2>{t('withOrgSelector.selectPrompt')}</h2>
            <SelectDropdown
              aria-label={t('withOrgSelector.organization')}
              id='orgSelect'
              isMenuSearchable
              onChange={onOrgSelect}
              options={orgOptions}
              placeholder={t('withOrgSelector.selectOrganization')}
              value=''
            />
          </>
        )}
      </Box>
    </Box>
  );
}
